import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { fmtDate } from "../lib/format";
import { ArrowLeft, Mail, Trash2, Zap } from "lucide-react";
import { adminToggleTrading } from "../lib/api";

const TradingToggle = ({ user, onDone }) => {
  const [busy, setBusy] = useState(false);
  const enabled = !!user.trading_enabled;
  const flip = async () => {
    setBusy(true);
    try {
      await adminToggleTrading(user.user_id, !enabled);
      toast.success(`Trading ${!enabled ? "enabled" : "revoked"} for ${user.email}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={flip}
      disabled={busy}
      data-testid={`trading-toggle-${user.user_id}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${
        enabled
          ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)]"
          : "border-[#3F3F46] text-[#71717A] hover:border-[#00E676] hover:text-[#00E676]"
      }`}
      title={enabled ? "Revoke trading access" : "Grant trading access"}
    >
      <Zap size={10} /> {enabled ? "ON" : "OFF"}
    </button>
  );
};

const DeleteUserButton = ({ user, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isAdmin = user.role === "admin";

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/users/${user.user_id}`, { withCredentials: true });
      const counts = r.data?.deleted || {};
      const summary = Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Deleted ${user.email}${summary ? ` (${summary})` : ""}`);
      const warnings = r.data?.stripe_warnings || [];
      if (warnings.length) toast.warning(`Stripe: ${warnings.join("; ")}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (isAdmin) {
    return <span className="text-[10px] text-[#71717A] font-mono">protected</span>;
  }
  if (confirming) {
    return (
      <div className="inline-flex items-center gap-1">
        <button
          data-testid={`delete-confirm-${user.user_id}`}
          onClick={doDelete}
          disabled={busy}
          className="text-[10px] px-2 py-0.5 border border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.12)] font-semibold uppercase tracking-wider"
        >
          {busy ? "…" : "Confirm"}
        </button>
        <button
          data-testid={`delete-cancel-${user.user_id}`}
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="text-[10px] px-2 py-0.5 text-[#71717A] hover:text-white"
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <button
      data-testid={`delete-user-btn-${user.user_id}`}
      onClick={() => setConfirming(true)}
      title={`Delete ${user.email} and all their data`}
      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-[#27272A] text-[#FF3B30] hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.06)]"
    >
      <Trash2 size={10} /> Delete
    </button>
  );
};


const GrantDaysControl = ({ userId, onDone }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const grant = async () => {
    const days = parseInt(val, 10);
    if (!days) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/grant-days`, { days }, { withCredentials: true });
      toast.success(`Granted ${days} days`);
      setVal("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-1">
      <input
        data-testid={`grant-days-input-${userId}`}
        type="number"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="±days"
        className="w-[60px] bg-[#0A0A0A] border border-[#27272A] px-1.5 py-0.5 text-[10px] font-mono text-right"
      />
      <button
        data-testid={`grant-days-btn-${userId}`}
        onClick={grant}
        disabled={!val || busy}
        className="btn-outline text-[10px] px-2 py-0.5"
      >
        Grant
      </button>
    </div>
  );
};

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [u, s, e, m] = await Promise.all([
        api.get("/admin/users", { withCredentials: true }),
        api.get("/admin/stats", { withCredentials: true }),
        api.get("/admin/events", { withCredentials: true }),
        api.get("/admin/contact-messages", { withCredentials: true }),
      ]);
      setUsers(u.data);
      setStats(s.data);
      setEvents(e.data);
      setMessages(m.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load admin data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="App">
      <Header />
      <main className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Admin</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Operations Console</h1>
          </div>
          <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="admin-back-btn">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        {error && (
          <div className="border border-[#FF3B30] bg-[rgba(255,59,48,0.06)] p-3 text-sm text-[#FFD9D6]">
            {error}
          </div>
        )}

        {stats && (
          <section className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#27272A] border border-[#27272A]" data-testid="admin-stats">
            <StatStrip label="Total Users" value={stats.total_users} />
            <StatStrip label="DAU (24h)" value={stats.dau_24h} tone="green" />
            <StatStrip label="Total Positions" value={stats.total_positions} />
            <StatStrip label="Active Plans" value={users.filter((u) => u.plan_status?.is_active).length} />
            <StatStrip label="Top Ticker" value={stats.top_tickers?.[0]?.symbol || "—"} />
          </section>
        )}

        <section className="panel" data-testid="admin-users-panel">
          <div className="panel-h">
            <div className="label-cap">Users ({users.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs grid-lines">
              <thead>
                <tr className="text-left border-b border-[#3F3F46] label-cap">
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Role</th>
                  <th className="px-3 py-2.5">Plan</th>
                  <th className="px-3 py-2.5 text-right">Days Left</th>
                  <th className="px-3 py-2.5 text-right">Positions</th>
                  <th className="px-3 py-2.5">Last Login</th>
                  <th className="px-3 py-2.5 text-center">Trading</th>
                  <th className="px-3 py-2.5 text-right">Grant Days</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#27272A]" data-testid={`admin-user-${u.user_id}`}>
                    <td className="px-3 py-2 truncate max-w-[200px]">{u.email}</td>
                    <td className="px-3 py-2">
                      {u.role === "admin" ? (
                        <span className="text-[#00E676] font-semibold">ADMIN</span>
                      ) : (
                        <span className="text-[#A1A1AA]">user</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        style={{
                          color: !u.plan_status?.is_active
                            ? "#FF3B30"
                            : u.plan_status?.plan === "yearly"
                            ? "#00E676"
                            : u.plan_status?.plan === "monthly"
                            ? "#00C853"
                            : "#FF9F0A",
                        }}
                      >
                        {u.plan_status?.status_label || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{u.plan_status?.days_left ?? "—"}d</td>
                    <td className="px-3 py-2 text-right">{u.positions}</td>
                    <td className="px-3 py-2 text-[#71717A]">{u.last_login ? fmtDate(u.last_login) : "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <TradingToggle user={u} onDone={loadAll} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <GrantDaysControl userId={u.user_id} onDone={loadAll} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <DeleteUserButton user={u} onDone={loadAll} />
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-[#71717A] p-6">No users yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel" data-testid="admin-events-panel">
          <div className="panel-h">
            <div className="label-cap">Activity Feed (latest {events.length})</div>
          </div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-[#27272A]">
            {events.map((e) => (
              <div key={e.event_id} className="px-4 py-2.5 flex items-baseline justify-between gap-4 text-xs">
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="font-mono text-[10px] text-[#71717A] whitespace-nowrap">{new Date(e.ts).toLocaleString()}</span>
                  <span className="font-mono text-[#007AFF] uppercase tracking-wider text-[10px]">{e.action}</span>
                  <span className="font-mono text-[#A1A1AA] truncate">{e.user?.email || e.user_id}</span>
                </div>
                <span className="font-mono text-[10px] text-[#71717A] truncate max-w-[40%]">
                  {Object.keys(e.payload || {}).length ? JSON.stringify(e.payload) : ""}
                </span>
              </div>
            ))}
            {events.length === 0 && (
              <div className="text-center text-[#71717A] p-6">No activity yet</div>
            )}
          </div>
        </section>

        {stats?.top_tickers?.length > 0 && (
          <section className="panel" data-testid="admin-top-tickers">
            <div className="panel-h">
              <div className="label-cap">Top Tracked Tickers</div>
            </div>
            <div className="p-4 flex flex-wrap gap-2">
              {stats.top_tickers.map((t) => (
                <div key={t.symbol} className="border border-[#27272A] px-3 py-1.5 font-mono text-xs">
                  <span className="font-bold">{t.symbol}</span>
                  <span className="text-[#71717A] ml-2">×{t.count}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="panel" data-testid="admin-contact-messages-panel">
          <div className="panel-h flex items-center justify-between">
            <div className="label-cap flex items-center gap-1.5">
              <Mail size={11} /> Contact Messages ({messages.length})
            </div>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-[#27272A]">
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`contact-msg-${m.id}`}
                className="px-4 py-3 space-y-1.5"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-xs font-semibold text-white truncate">{m.subject}</span>
                    {!m.email_sent && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border"
                        style={{ color: "#FF9F0A", borderColor: "#FF9F0A" }}
                        title={m.email_configured ? "SMTP send failed" : "SMTP not configured"}
                      >
                        DB-only
                      </span>
                    )}
                    {m.email_sent && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border"
                        style={{ color: "#00E676", borderColor: "#00E676" }}
                      >
                        Emailed
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-[10px] text-[#71717A] whitespace-nowrap">
                    {new Date(m.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 text-[10px] font-mono text-[#A1A1AA]">
                  <span className="text-white">{m.from_name}</span>
                  <span>·</span>
                  <a href={`mailto:${m.from_email}?subject=Re:%20${encodeURIComponent(m.subject)}`} className="text-[#007AFF] hover:underline">
                    {m.from_email}
                  </a>
                  {m.user_email && m.user_email !== m.from_email && (
                    <span className="text-[#71717A]">(account: {m.user_email})</span>
                  )}
                </div>
                <p className="text-xs text-[#A1A1AA] whitespace-pre-wrap leading-relaxed">
                  {m.message}
                </p>
              </div>
            ))}
            {messages.length === 0 && (
              <div className="text-center text-[#71717A] p-6 text-xs">No contact messages yet</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

const StatStrip = ({ label, value, tone = "neutral" }) => (
  <div className="bg-[#0A0A0A] p-4">
    <div className="label-cap">{label}</div>
    <div className={`font-mono text-2xl mt-1 ${tone === "green" ? "text-[#00E676]" : "text-white"}`}>
      {value}
    </div>
  </div>
);
